// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { Description, Icon, Row, SmPadding, XsPadding, XsSpacing, XxsPadding } from '@alto/design-system';
import React from 'react';
import { ActivityIndicator } from 'react-native';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { getNextAvailableDateDisplayCopy } from '~shared/features/next-available-date/helpers/getNextAvailableDateDisplayCopy';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { useSelectorShared } from '~shared/store';
import { useEssentialsCartThreshold } from '../../essentials';

type Props = {
  readonly hideNextAvailableDate?: boolean;
};
export const CartFooterDescriptionText = ({ hideNextAvailableDate }: Props) => {
  const { orderMinimumRemainder, showEssentialsThreshold, minimumFooterCopy, updatingPricing } =
    useEssentialsCartThreshold();
  const { earliestAvailableDate, earliestAvailableDateReason } = useCartNextAvailableDate();
  const { fetchNextAvailableDatesLoading } = useSelectorShared(getLoading);
  const refreshing = (
    <XsPadding topPadding={SPACING.STATIC.NONE}>
      <Row centerHorizontally>
        <ActivityIndicator />
        <XsSpacing />
        <Description>Refreshing...</Description>
      </Row>
    </XsPadding>
  );

  if (showEssentialsThreshold) {
    if (updatingPricing) {
      return refreshing;
    }

    return (
      <XsPadding topPadding={SPACING.STATIC.NONE}>
        <Description center>
          {minimumFooterCopy} <Description fontFamily="semibold">${orderMinimumRemainder}</Description> more to checkout
        </Description>
      </XsPadding>
    );
  }

  if (hideNextAvailableDate) {
    return null;
  }

  const nextAvailableDateCopy = getNextAvailableDateDisplayCopy({
    earliestAvailableDate,
    earliestAvailableReason: earliestAvailableDateReason,
    isLoading: fetchNextAvailableDatesLoading,
  });

  if (nextAvailableDateCopy === 'Refreshing...') {
    return refreshing;
  }

  if (!nextAvailableDateCopy) {
    return null;
  }

  return (
    <SmPadding topPadding={SPACING.STATIC.NONE}>
      <Row
        centerHorizontally
        centerVertically
      >
        <Icon
          name="calendar-small"
          type="grey"
        />
        <XxsPadding />
        <Description center>{nextAvailableDateCopy}</Description>
      </Row>
    </SmPadding>
  );
};
